import { Button } from "@datamole/wds-component-button";
import { faDirections } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext } from "react";

import { InfoPopoverContext } from "../info-popover/popover.context";

import { findClosestNavPoint } from "./NavigateAction.utils";
import { getCost, getShortestPath, Node } from "./navigation.utils";

import { navEdges, navPoints, rooms, floors } from "@/features/storage/data-provider";

export const NavigateAction = ({ startRoomId, endRoomId }: { startRoomId: string; endRoomId: string }) => {
    const { setTrigger } = useContext(InfoPopoverContext);

    const navigate = () => {
        const startPoint = findClosestNavPoint(startRoomId, navPoints, rooms);
        const endPoint = findClosestNavPoint(endRoomId, navPoints, rooms);

        if (!startPoint || !endPoint) {
            console.warn("Could not find start or end point for navigation");
            return;
        }

        const nodes: Record<string, Node> = Object.fromEntries(
            navPoints.map((point) => [
                point.id,
                {
                    identifier: point.id,
                    edges: navEdges
                        .filter((edge) => edge.id1 === point.id || edge.id2 === point.id)
                        .map((accessible) => {
                            const end = accessible.id1 === point.id ? accessible.id2 : accessible.id1;
                            const neighbor = navPoints.find((p) => p.id === end);
                            const neighborCost = neighbor ? getCost(point.location, neighbor.location) : Infinity;
                            const isNeighborStairs =
                                neighbor && floors.find((f) => f.stairNavPointId === neighbor.id) !== undefined;
                            return {
                                start: point.id,
                                end: end,
                                cost: !isNeighborStairs ? neighborCost : 1e6,
                            };
                        }),
                },
            ]),
        );

        // Multi-floor navigation logic
        if (startPoint.floorId !== endPoint.floorId) {
            // Find stairs on both floors
            const startFloor = floors.find((f) => f.id === startPoint.floorId);
            const endFloor = floors.find((f) => f.id === endPoint.floorId);

            if (startFloor?.stairNavPointId && endFloor?.stairNavPointId) {
                const startStair = navPoints.find((p) => p.id === startFloor.stairNavPointId);
                const endStair = navPoints.find((p) => p.id === endFloor.stairNavPointId);

                if (startStair && endStair) {
                    const path1 = getShortestPath(startPoint.id, startStair.id, nodes);
                    const path2 = getShortestPath(endStair.id, endPoint.id, nodes);

                    if (path1 && path2) {
                        setTrigger({ type: "path", path: [...path1, ...path2] });
                    }
                    return;
                }
            }
        }

        const path = getShortestPath(startPoint.id, endPoint.id, nodes);
        if (path) {
            setTrigger({ type: "path", path });
        }
    };

    return (
        <Button onClick={() => navigate()}>
            <Button.Icon>
                <FontAwesomeIcon icon={faDirections} />
            </Button.Icon>
            <Button.Content>Navigate</Button.Content>
        </Button>
    );
};
